import React, { useEffect, useRef, useState } from 'react';
import { Search, TrendingUp, Users, Trash2, ChevronDown, Globe, Moon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useBlockUserMutation } from '@/store/api/blockApi';
import { useSearchUsersQuery } from '@/store/api/SearchApi';
import { toast } from 'react-hot-toast';
import Loader from '@/components/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useUsersQuery } from '@/store/api/usersAPi';
import DateRangeFilter from '@/pages/user/components/DateFilter';
import { useNewDebounce } from '@/hooks/useNewDebounce';
import { useDeleteUserMutation } from '@/store/api/deleteUserApi';
import { formatShowDate, formatShowTime, formatTimezone } from '@/utils/helperUtils';

// ─── Columns (no Created, Status, View, Action) ──────────────────────────────

const buildColumns = (): ColumnDef<any>[] => [
  {
    id: 'sno',
    header: 'S. No.',
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = (table.options.meta as any)?.pagination ?? {
        pageIndex: 0,
        pageSize: 0,
      };
      return pageIndex * pageSize + row.index + 1;
    },
  },
  { accessorKey: 'fullName', header: 'Full Name' },
  { accessorKey: 'email', header: 'Email' },
  { header: 'DOB', accessorFn: (row) => formatShowDate(row.meta?.dateOfBirth) || '-' },
  { header: 'Time of Birth', accessorFn: (row) => formatShowTime(row.meta?.timeOfBirth) || '-' },
  { header: 'Latitude', accessorFn: (row) => row.meta?.latitude ?? '-' },
  { header: 'Longitude', accessorFn: (row) => row.meta?.longitude ?? '-' },
  { header: 'Timezone', accessorFn: (row) => formatTimezone(row.meta?.timezone) },
  {
    accessorKey: 'country',
    header: 'Country',
    cell: ({ row }) => {
      const v = row.original.country || '-';
      return <div title={v}>{v.length > 15 ? v.slice(0, 15) + '...' : v}</div>;
    },
  },
  {
    accessorKey: 'state',
    header: 'State',
    cell: ({ row }) => {
      const v = row.original.state || '-';
      return <div title={v}>{v.length > 18 ? v.slice(0, 18) + '...' : v}</div>;
    },
  },
  { accessorKey: 'city', header: 'City', cell: ({ row }) => row.original.city || '-' },
  { header: 'Moon Sign', accessorFn: (row) => row.meta?.sunSign || '-' },
  { header: 'Ascendant', accessorFn: (row) => row.meta?.ascendant || '-' },
];

const PRESET_COUNTRIES = [
  'India',
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'United Arab Emirates',
  'Singapore',
  'New Zealand',
  'South Africa',
  'Nigeria',
  'Pakistan',
  'Bangladesh',
  'Sri Lanka',
  'Nepal',
  'Malaysia',
  'Philippines',
  'Indonesia',
  'Brazil',
  'Mexico',
  'Italy',
  'Spain',
  'Netherlands',
  'Sweden',
  'Norway',
  'Denmark',
  'Switzerland',
  'Ireland',
  'Saudi Arabia',
  'Qatar',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Kenya',
  'Ghana',
  'Tanzania',
  'Ethiopia',
  'Japan',
  'China',
];

function CountryFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = countrySearch.trim()
    ? PRESET_COUNTRIES.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase()))
    : PRESET_COUNTRIES;

  // show custom searched country as extra option if not in presets
  const showCustom =
    countrySearch.trim() &&
    !PRESET_COUNTRIES.some((c) => c.toLowerCase() === countrySearch.trim().toLowerCase());

  const displayLabel = value || 'Country';

  return (
    <div className="space-y-2 relative" ref={ref}>
      <Label>Country</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="
          h-10 w-[160px] flex items-center justify-between gap-2
          border border-[#001B6B] rounded-lg px-3 py-2 text-sm
          bg-white hover:bg-gray-50 transition-colors
        "
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{displayLabel}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-[200px] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {/* search inside dropdown */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                autoFocus
                type="text"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-0"
              />
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto">
            {/* Clear option */}
            <button
              onClick={() => {
                onChange('');
                setOpen(false);
                setCountrySearch('');
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#001B6B]/10 transition-colors ${!value ? 'bg-[#001B6B]/5 font-medium text-[#001B6B]' : 'text-gray-600'}`}
            >
              All Countries
            </button>

            {filtered.map((c) => (
              <button
                key={c}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setCountrySearch('');
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#001B6B]/10 transition-colors flex items-center gap-2 ${value === c ? 'bg-[#001B6B]/5 font-medium text-[#001B6B]' : 'text-gray-700'}`}
              >
                <Globe className="w-3.5 h-3.5 opacity-50 shrink-0" />
                {c}
              </button>
            ))}

            {/* custom country from search */}
            {showCustom && (
              <button
                onClick={() => {
                  onChange(countrySearch.trim());
                  setOpen(false);
                  setCountrySearch('');
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#001B6B]/10 transition-colors flex items-center gap-2 text-gray-700 border-t border-gray-100"
              >
                <Search className="w-3.5 h-3.5 opacity-50 shrink-0" />
                Search "{countrySearch.trim()}"
              </button>
            )}

            {filtered.length === 0 && !showCustom && (
              <p className="px-3 py-3 text-sm text-gray-400 text-center">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const RASHIS = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
];

function MoonSignFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="space-y-2 relative" ref={ref}>
      <Label>Moon Sign</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-10 w-[140px] flex items-center justify-between gap-2 border border-[#001B6B] rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 transition-colors"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || 'Moon Sign'}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-[160px] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            <button
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#001B6B]/10 transition-colors ${!value ? 'bg-[#001B6B]/5 font-medium text-[#001B6B]' : 'text-gray-600'}`}
            >
              All Signs
            </button>
            {RASHIS.map((rashi) => (
              <button
                key={rashi}
                onClick={() => {
                  onChange(rashi);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#001B6B]/10 transition-colors flex items-center gap-2 ${value === rashi ? 'bg-[#001B6B]/5 font-medium text-[#001B6B]' : 'text-gray-700'}`}
              >
                <Moon className="w-3.5 h-3.5 opacity-50 shrink-0" />
                {rashi}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="w-[250px] min-w-[250px] max-w-[250px] h-[100px] bg-white inner-shadow-blue rounded-xl px-6 py-5 transition-all duration-500 hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-5 h-5 opacity-80 text-[#09226b]" />
      </div>
    </div>
  );
}

// ─── Confirm Dialog ──────────────────────────────────────────────────────────

function ConfirmDeleteDialog({
  count,
  label,
  onConfirm,
  onCancel,
  isLoading,
}: {
  count: number;
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-5 text-white flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold font-spectral">Confirm Delete</p>
            <p className="text-white/70 text-xs mt-0.5">This action cannot be undone</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">{label}</p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete {count > 1 ? `(${count})` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function DeleteUsersPage() {
  const [reportType, setReportType] = useState<string>('all');
  const [userLoginType, setUserLoginType] = useState<string>('all');
  const [country, setCountry] = useState<string>('');
  const [moonSign, setMoonSign] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [dateError, setDateError] = useState<string>('');
  const [showLoader, setShowLoader] = useState(false);

  // pending delete state: null = closed, 'selected' = delete selected, 'all' = delete all
  const [pendingDelete, setPendingDelete] = useState<{
    ids: string[];
    mode: 'selected' | 'all';
  } | null>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const debouncedSearch = useNewDebounce(search, 400);

  const {
    data,
    isLoading,
    isFetching: isUsersFetching,
    refetch,
  } = useUsersQuery({
    reportType,
    userLoginType,
    take: pageSize,
    skip: pageIndex * pageSize,
  });

  const searchUsersQuery = useSearchUsersQuery(
    {
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(userLoginType !== 'all' && { userLoginType }),
      ...(startDate &&
        endDate && {
          fromDate: startDate.toISOString(),
          toDate: endDate.toISOString(),
        }),
      take: pageSize,
      skip: pageIndex * pageSize,
    },
    { skip: !debouncedSearch && (!startDate || !endDate) }
  );

  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [blockUser] = useBlockUserMutation();

  const totalUsers = data?.count ?? 0;
  const blockedUsers = data?.totalBlocked ?? 0;
  const calculatedActiveUsers = totalUsers - blockedUsers;

  const isSearching = !!debouncedSearch;
  const isDateFiltering = !!startDate && !!endDate;
  const isFiltering = isSearching || isDateFiltering;
  const isPaginationFetching = isFiltering ? searchUsersQuery.isFetching : isUsersFetching;
  const tableData = isFiltering ? searchUsersQuery.data : data;

  const rawUsers = tableData?.data ?? [];
  const users = React.useMemo(() => {
    let list = rawUsers;
    if (isSearching && reportType === 'user')
      list = list.filter((u: any) => u.status === 'Blocked');
    if (isSearching && reportType === 'analytics')
      list = list.filter((u: any) => u.status === 'Active');
    if (country) list = list.filter((u: any) => u.country?.toLowerCase() === country.toLowerCase());
    if (moonSign)
      list = list.filter((u: any) => u.meta?.sunSign?.toLowerCase() === moonSign.toLowerCase());
    return list;
  }, [rawUsers, isSearching, reportType, country, moonSign]);

  const handleDateChange = ([start, end]: [Date | null, Date | null]) => {
    if (!start && !end) {
      setStartDate(null);
      setEndDate(null);
      setDateError('');
      return;
    }
    setStartDate(start);
    setEndDate(end);
    if (!start || !end) {
      setDateError('Please select both start and end date');
      return;
    }
    if (end < start) {
      setDateError('Start date cannot be greater than end date');
      return;
    }
    if (end > new Date()) {
      setDateError('Future dates are not allowed');
      return;
    }
    setDateError('');
  };

  // Delete selected rows
  const handleDeleteSelected = (rows: any[]) => {
    const ids = rows.map((u) => u.userId || u.id || u._id).filter(Boolean);
    if (!ids.length) return;
    setPendingDelete({ ids, mode: 'selected' });
  };

  // Delete ALL users (fetch all, then delete sequentially)
  const handleDeleteAll = async () => {
    // We'll collect all IDs from current visible list as a starting point
    // and confirm before proceeding
    const ids = users.map((u: any) => u.userId || u.id || u._id).filter(Boolean);
    setPendingDelete({ ids, mode: 'all' });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { ids } = pendingDelete;
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        await deleteUser(id).unwrap();
        successCount++;
      } catch {
        failCount++;
      }
    }

    setPendingDelete(null);
    if (successCount > 0)
      toast.success(`${successCount} user${successCount > 1 ? 's' : ''} deleted successfully`);
    if (failCount > 0) toast.error(`${failCount} deletion${failCount > 1 ? 's' : ''} failed`);
    if (isFiltering) searchUsersQuery.refetch();
    refetch();
  };

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, startDate, endDate]);
  useEffect(() => {
    setPageIndex(0);
  }, [reportType, userLoginType, country, moonSign]);

  const pageLoading = isLoading || isPaginationFetching || showLoader;
  const columns = React.useMemo(() => buildColumns(), []);

  return (
    <div className="space-y-6 px-6">
      {pageLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {pendingDelete && (
        <ConfirmDeleteDialog
          count={pendingDelete.ids.length}
          label={
            pendingDelete.mode === 'all'
              ? `Are you sure you want to permanently delete all ${pendingDelete.ids.length} users on this page?`
              : `Are you sure you want to permanently delete ${pendingDelete.ids.length} selected user${pendingDelete.ids.length > 1 ? 's' : ''}?`
          }
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
          isLoading={isDeleting}
        />
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
      </div>

      <div className="flex flex-wrap gap-6">
        <StatCard label="Total Users" value={isLoading ? '—' : totalUsers} icon={Users} />
        <StatCard
          label="Active Users"
          value={isLoading ? '—' : calculatedActiveUsers}
          icon={TrendingUp}
        />
        <StatCard label="Blocked Users" value={isLoading ? '—' : blockedUsers} icon={Users} />
      </div>

      <Card>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-[1fr_auto] mt-4">
            {/* Search */}
            <div className="space-y-2 relative max-w-[360px]">
              <Label htmlFor="search">Search User</Label>
              <Search
                size={20}
                className="absolute right-3 top-1/2 -translate-y-[20%] text-muted-foreground"
              />
              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by Name / Email"
                className="w-full border border-[#001B6B] rounded-lg px-3 py-2 pr-10 outline-none focus:ring-0"
              />
            </div>

            {/* Filters */}
            <div className="flex items-start justify-end gap-4">
              {/* Delete All button */}
              <div className="space-y-2">
                <Label className="invisible">.</Label>
                <button
                  onClick={handleDeleteAll}
                  disabled={users.length === 0}
                  className="
                    h-10 px-4 text-sm font-semibold rounded-lg flex items-center gap-2
                    bg-red-600 text-white border border-red-600
                    hover:bg-red-700 transition-all duration-300
                    disabled:opacity-40 disabled:cursor-not-allowed
                  "
                >
                  <Trash2 className="w-4 h-4" />
                  Delete All
                </button>
              </div>

              <CountryFilter value={country} onChange={setCountry} />

              <MoonSignFilter value={moonSign} onChange={setMoonSign} />

              <div className="space-y-2 w-[140px]">
                <Label>Users Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger className="w-full border border-[#001B6B] rounded-lg px-3 py-2 ring-0 focus:ring-0 shadow-none">
                    <SelectValue placeholder="Select users type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="analytics">Active users</SelectItem>
                    <SelectItem value="user">Block users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 w-[160px]">
                <Label>User Login Type</Label>
                <Select value={userLoginType} onValueChange={setUserLoginType}>
                  <SelectTrigger className="w-full border border-[#001B6B] rounded-lg px-3 py-2 ring-0 focus:ring-0 shadow-none">
                    <SelectValue placeholder="Select user type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="Registered">Registered users</SelectItem>
                    <SelectItem value="Google">Google users</SelectItem>
                    <SelectItem value="Guest">Guest users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <DateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  error={dateError}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsContent value="all" className="space-y-4" />
      </Tabs>

      <div className="w-full font-lato">
        <DataTable
          data={users}
          columns={columns}
          enableSearch={false}
          enableFilters={false}
          enableColumnVisibility={false}
          enableSelection={true}
          onSelectionChange={setSelectedRows}
          onDelete={handleDeleteSelected}
          manualPagination={true}
          pageSize={pageSize}
          totalCount={tableData?.count ?? 0}
          pageCount={Math.ceil((tableData?.count ?? 0) / pageSize)}
          onPaginationChange={({ pageIndex, pageSize }) => {
            setPageIndex(pageIndex);
            setPageSize(pageSize);
          }}
          isLoading={pageLoading}
        />
      </div>
    </div>
  );
}
